import { useEffect, useMemo, useRef, useState } from 'react';
import { api, runWithAuditReason } from '../../api/client';
import { FormTitle } from '../../components/FormTitle';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../components/confirm';
import { ComboSelect } from '../../components/ComboSelect';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNavActions } from '../../hooks/useNavActions';
import { DualListPickerDialog } from '../../components/DualListPicker';
import { ViewToggle } from '../../components/ViewToggle';
import { CellContextMenu, canBulkCopy } from '../../components/gridEdit';
import { exportRows } from '../../utils/exportData';
import './security.css';

// ─────────────────────────────────────────────────────────────────────────
// User & Security → Module Access Control + Role-Based MAC
// (ALPACA List → full-page Detail anatomy — rebuilt 2026-07-02)
// ─────────────────────────────────────────────────────────────────────────
//   "levels" tab (Module Access Control): LIST (landing) = MACID · Description
//   in the shared <AppDataGrid>, sorted MACID asc; toolbar = Audit · New ·
//   Edit · Delete · Refresh · Export to.  Edit/row-click opens a FULL-PAGE
//   detail (no popup): 2-column header (MAC ID auto / Module lookup / Module
//   Index read-only · MAC Name* / Description) + the full-width Navigation-
//   Item rights grid (Read/Write/Create/Delete inline checkboxes; Link/Unlink
//   via the shared DualListPickerDialog).
//
//   "matrix" tab (Role-Based MAC): stays a single matrix VIEW — rows = MACs,
//   columns = roles (unbounded), inline checkboxes; toolbar = Save + Export to.
//
//   ModuleAccessControl = a named (module, access-level) record. The Role-Based
//   MAC matrix assigns each MAC to roles via the canonical MAC↔role junction
//   (RoleNavigationPermissionRoles_ModuleAccessControlMACIDs). ALPACA rebuilds
//   its 30-bit RoleMACConfig mirror from that junction on screen open, and
//   cascades downstream nav-permission regeneration on its next save.
// ─────────────────────────────────────────────────────────────────────────

function lcKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(lcKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k.toLowerCase()] = lcKeys(val);
    return out;
  }
  return v;
}
const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
  ?? (e as { message?: string }).message ?? 'Request failed.';

type Mac = { oid: string; macId: string | null; moduleName: string | null; moduleIndex: number; macName: string | null; description: string | null; roleCount: number };
type ModuleRef = { moduleName: string; moduleIndex: number };
type MatrixRole = { id: string; name: string };
type MatrixRow = { macOid: string; macId: string | null; moduleName: string | null; moduleIndex: number; macName: string | null; roleIds: string[] };
type NavPerm = { navItemOid: string; caption: string; read: boolean; write: boolean; create: boolean; delete: boolean };
type NavRef = { oid: string; caption: string };

const lc = (s: string) => s.toLowerCase();

// lcKeys lowercases every key; map that lowercase bag back into our typed shapes
// (reading camelCase off lcKeys output is what left the legacy MAC grids blank).
type Rec = Record<string, unknown>;
const arr = (data: unknown): Rec[] => (((lcKeys(data) as { data?: Rec[] }).data) ?? []);
const one = (data: unknown): Rec | undefined => ((lcKeys(data) as { data?: Rec }).data);
const mapMac = (m: Rec): Mac => ({
  oid: String(m.oid), macId: (m.macid as string) ?? null, moduleName: (m.modulename as string) ?? null,
  moduleIndex: Number(m.moduleindex ?? 0), macName: (m.macname as string) ?? null,
  description: (m.description as string) ?? null, roleCount: Number(m.rolecount ?? 0),
});
const mapMatrixRole = (r: Rec): MatrixRole => ({ id: String(r.id), name: String(r.name ?? '') });
const mapNav = (r: Rec): NavPerm => ({
  navItemOid: String(r.navitemoid), caption: String(r.caption ?? '—'),
  read: !!r.read, write: !!r.write, create: !!r.create, delete: !!r.delete,
});
const mapNavRef = (r: Rec): NavRef => ({ oid: String(r.oid), caption: String(r.caption ?? '') });
const mapMatrixRow = (r: Rec): MatrixRow => ({
  macOid: String(r.macoid), macId: (r.macid as string) ?? null, moduleName: (r.modulename as string) ?? null,
  moduleIndex: Number(r.moduleindex ?? 0), macName: (r.macname as string) ?? null,
  roleIds: ((r.roleids as string[]) ?? []),
});
const mapModule = (m: Rec): ModuleRef => ({ moduleName: String(m.modulename ?? ''), moduleIndex: Number(m.moduleindex ?? 0) });

const RIGHT_FLAGS: { key: 'read' | 'write' | 'create' | 'delete'; label: string }[] = [
  { key: 'read', label: 'Read' },
  { key: 'write', label: 'Write' },
  { key: 'create', label: 'Create' },
  { key: 'delete', label: 'Delete' },
];

export default function MacAdminPage({ tab: initialTab = 'levels' }: { tab?: 'levels' | 'matrix' }) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const confirm = useConfirm();

  // ALPACA parity: Module Access Control and Role-Based MAC are two separate
  // ListViews with no shared affordance between them — the tab is fixed by
  // which nav entry launched this page, never switchable in-place (2026-07-20).
  const tab = initialTab;
  // MAC rights of the signed-in user on THIS screen (Module Access Control / Role-Based MAC). A Read-only
  // role keeps the page but loses New / Edit / Delete / Copy / Save — the API refuses them too (403).
  const permLevels = useNavActions('sec-mac');
  const permMatrix = useNavActions('sec-rolemac');
  const perm = tab === 'matrix' ? permMatrix : permLevels;
  const [macs, setMacs] = useState<Mac[]>([]);
  const [modules, setModules] = useState<ModuleRef[]>([]);
  const [roles, setRoles] = useState<MatrixRole[]>([]);
  const [rows, setRows] = useState<MatrixRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // levels editor (full-page detail).  'list' = landing grid.  Opening an
  // EXISTING record shows the detail READ-ONLY first ('view' — ALPACA parity,
  // BO UI Standard v2 2026-07-02); the detail's Edit button switches to
  // 'edit'.  New goes straight to 'new' (edit-style bar).
  const [selOid, setSelOid] = useState<string | null>(null);
  const [mode, setMode] = useState<'list' | 'new' | 'view' | 'edit'>('list');
  const [form, setForm] = useState({ moduleName: '', moduleIndex: 0, macName: '', description: '' });
  const [saving, setSaving] = useState(false);
  // Inline field-level validation errors (§18.6 Tier 3 — never a modal).
  const [formErrors, setFormErrors] = useState<{ macName?: string; moduleName?: string }>({});
  // list selection (checkbox column) — drives toolbar Edit / Delete
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  // NavigationItem permission child grid (ALPACA MAC DetailView parity)
  const [navItems, setNavItems] = useState<NavPerm[]>([]);
  const [allNav, setAllNav] = useState<NavRef[]>([]);
  const [selNav, setSelNav] = useState<Set<string | number>>(new Set());
  const [navPickerOpen, setNavPickerOpen] = useState(false);


  // matrix edits: macOid -> Set(roleId) ; dirty macOids
  const [grid, setGrid] = useState<Record<string, string[]>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [savingGrid, setSavingGrid] = useState(false);
  const [matrixFilter, setMatrixFilter] = useState('');

  const load = () => {
    setLoading(true); setErr(null);
    const t0 = performance.now();
    Promise.all([
      api.get('/api/v1/security/mac'),
      api.get('/api/v1/security/mac/matrix'),
      modules.length === 0 ? api.get('/api/v1/security/mac/modules') : Promise.resolve(null),
    ]).then(([ml, mx, md]) => {
      setMacs(arr(ml.data).map(mapMac));
      const matrix = one(mx.data);
      if (matrix) {
        const mrows = ((matrix.rows as Rec[]) ?? []).map(mapMatrixRow);
        setRoles(((matrix.roles as Rec[]) ?? []).map(mapMatrixRole));
        setRows(mrows);
        const g: Record<string, string[]> = {};
        for (const r of mrows) g[r.macOid] = (r.roleIds ?? []).map(lc);
        setGrid(g); setDirty(new Set());
      }
      if (md) setModules(arr(md.data).map(mapModule));
    }).catch(e => setErr(errMsg(e))).finally(() => { setLoadMs(performance.now() - t0); setLoading(false); });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── levels tab ──────────────────────────────────────────────────────────
  const openNew = () => {
    setMode('new'); setSelOid(null);
    setForm({ moduleName: '', moduleIndex: 0, macName: '', description: '' });
    setNavItems([]); setSelNav(new Set());
    setFormErrors({});
  };
  // Opening from the list (row click / pencil / prev-next / Detail tab) always
  // lands in read-only VIEW mode; the detail's Edit button unlocks it.
  const openEdit = (m: Mac) => {
    setSelOid(m.oid); setMode('view'); setSelNav(new Set());
    setForm({ moduleName: m.moduleName ?? '', moduleIndex: m.moduleIndex ?? 0, macName: m.macName ?? '', description: m.description ?? '' });
    // Load this MAC's per-nav-item permission template (child grid) + the picker source.
    setNavItems([]);
    api.get(`/api/v1/security/mac/${m.oid}/navitems`).then(r => setNavItems(arr(r.data).map(mapNav))).catch(() => setNavItems([]));
  };
  const closeDetail = () => { setMode('list'); setSelOid(null); };

  // The Link picker offers only the forms of THIS MAC's module (owner request 2026-10-28: a Reporting MAC must not list all
  // 432 forms; G5 MACController does the same) — the server resolves the module's forms, so it is asked each time the picker
  // opens, because the module of a New / Copied MAC can still be changed in the form.
  const openNavPicker = async () => {
    if (!form.moduleName) { notify.warning('Select a Module first — the Link list shows only the forms of that module.'); return; }
    try {
      const r = await api.get('/api/v1/security/mac/navigation-items', { params: { module: form.moduleName } });
      setAllNav(arr(r.data).map(mapNavRef));
      setNavPickerOpen(true);
    } catch (e) { notify.error(errMsg(e)); }
  };

  // Detail-view toolbar parity (ALPACA ModuleAccessControl_DetailView): New /
  // Delete / Refresh alongside Edit — not just Edit + Close.
  const refreshCurrent = () => {
    load();
    if (!selOid) return;
    api.get(`/api/v1/security/mac/${selOid}`).then(r => {
      const d = one(r.data);
      if (d) {
        const m = mapMac(d);
        setForm({ moduleName: m.moduleName ?? '', moduleIndex: m.moduleIndex ?? 0, macName: m.macName ?? '', description: m.description ?? '' });
      }
    }).catch(() => { /* keep current form on failure */ });
    api.get(`/api/v1/security/mac/${selOid}/navitems`).then(r => setNavItems(arr(r.data).map(mapNav))).catch(() => { /* keep current rows */ });
  };
  const deleteCurrent = async () => {
    if (!curMac) return;
    const label = curMac.macId ?? curMac.macName ?? '';
    if (!(await confirm.confirm({ title: 'Delete access level?', message: `Delete "${label}"? (soft-delete — recoverable)` }))) return;
    try {
      await api.delete(`/api/v1/security/mac/${curMac.oid}`);
      notify.success('Access level deleted.');
      closeDetail();
      load();
    } catch (e) { notify.error(errMsg(e)); }
  };

  // Copy MAC — opens the full detail form pre-filled from the source (module,
  // module index, description, nav-item rights) with MAC Name left blank for
  // the user to enter. The existing Save flow then creates the new record.
  const copyMacSelected = async () => {
    if (sel.size !== 1) return;
    const m = macs.find(x => x.oid === String(Array.from(sel)[0]));
    if (!m) return;
    // Pre-fill form fields from source; MAC Name intentionally empty.
    setMode('new'); setSelOid(null);
    setForm({ moduleName: m.moduleName ?? '', moduleIndex: m.moduleIndex ?? 0, macName: '', description: m.description ?? '' });
    setSelNav(new Set());
    setFormErrors({});
    // Load the source's nav-item permission template into the rights grid.
    try {
      const r = await api.get(`/api/v1/security/mac/${m.oid}/navitems`);
      setNavItems(arr(r.data).map(mapNav));
    } catch { setNavItems([]); }
  };

  const toggleNav = (navOid: string, field: 'read' | 'write' | 'create' | 'delete') =>
    setNavItems(items => items.map(i => i.navItemOid === navOid ? { ...i, [field]: !i[field] } : i));

  // ── BO UI Standard v2 §9 — right-click bulk copy (shared CellContextMenu).
  //    Rights grid + Role Matrix edits are LOCAL state until the form's existing
  //    audited Save persists them — so a bulk copy lands as ONE audit row through
  //    that save (the standard's one-audit-row-per-bulk-copy semantics).
  type RightKey = 'read' | 'write' | 'create' | 'delete';
  const [rightsCtx, setRightsCtx] = useState<{ x: number; y: number; key: RightKey; label: string; value: boolean } | null>(null);
  const [matrixCtx, setMatrixCtx] = useState<{ x: number; y: number; roleId: string; roleName: string; value: boolean } | null>(null);

  const openRightsCtx = (e: React.MouseEvent, key: RightKey, label: string, value: boolean) => {
    e.preventDefault(); e.stopPropagation();
    if (mode === 'view') return;                               // bulk copy only in EDIT mode
    if (!canBulkCopy(value)) { notify.warning('Cell is empty.'); return; }
    setRightsCtx({ x: e.clientX, y: e.clientY, key, label, value });
  };
  const applyRightsAll = (key: RightKey, value: boolean) =>
    setNavItems(items => items.map(i => ({ ...i, [key]: value })));
  const applyRightsSelected = (key: RightKey, value: boolean) => {
    if (selNav.size === 0) { notify.warning('Select rows first.'); return; }
    setNavItems(items => items.map(i => (selNav.has(i.navItemOid) ? { ...i, [key]: value } : i)));
  };

  const openMatrixCtx = (e: React.MouseEvent, roleId: string, roleName: string, value: boolean) => {
    e.preventDefault(); e.stopPropagation();
    if (!canBulkCopy(value)) { notify.warning('Cell is empty.'); return; }
    setMatrixCtx({ x: e.clientX, y: e.clientY, roleId, roleName, value });
  };
  // Copy the clicked checkbox's value down its ROLE column for every MAC.
  // (The matrix has no row-selection checkboxes, so only Copy to All Cells is
  // offered.)  Existing dirty-tracking + Save persists.
  const applyMatrixAll = (roleId: string, value: boolean) => {
    const rid = lc(roleId);
    setDirty(d => {
      const nd = new Set(d);
      for (const r of rows) if ((grid[r.macOid] ?? []).includes(rid) !== value) nd.add(r.macOid);
      return nd;
    });
    setGrid(g => {
      const next: Record<string, string[]> = { ...g };
      for (const r of rows) {
        const cur = next[r.macOid] ?? [];
        const has = cur.includes(rid);
        if (value && !has) next[r.macOid] = [...cur, rid];
        else if (!value && has) next[r.macOid] = cur.filter(x => x !== rid);
      }
      return next;
    });
  };

  // Link (DualListPickerDialog OK) — keep the still-selected rows (with their
  // edited flags) and add newly picked nav items with the same default flags
  // the old single Link used (read on, everything else off).
  const applyNavPick = (ids: string[]) => {
    setNavItems(prev => {
      const picked = new Set(ids);
      const kept = prev.filter(i => picked.has(i.navItemOid));
      const existing = new Set(prev.map(i => i.navItemOid));
      const added: NavPerm[] = [];
      for (const id of ids) {
        if (existing.has(id)) continue;
        const ref = allNav.find(n => n.oid === id);
        if (ref) added.push({ navItemOid: ref.oid, caption: ref.caption, read: true, write: false, create: false, delete: false });
      }
      return [...kept, ...added];
    });
    setSelNav(new Set());
    setNavPickerOpen(false);
  };
  const unlinkSelectedNav = () => {
    if (selNav.size === 0) { notify.warning('Select at least one navigation item to unlink.'); return; }
    setNavItems(items => items.filter(i => !selNav.has(i.navItemOid)));
    setSelNav(new Set());
  };

  // ALPACA resetMACAction (MACController.cs, port 2026-07-02) — blocked while any
  // role links the MAC; otherwise clears the detail LOCALLY (module / name /
  // description + the nav-item rights grid).  Nothing persists until the
  // existing audited Save — exactly the legacy in-memory behaviour.
  const resetMac = () => {
    if ((curMac?.roleCount ?? 0) > 0) {
      notify.info('The current MAC cannot be reset, as it is already linked to a role.');
      return;
    }
    setForm({ moduleName: '', moduleIndex: 0, macName: '', description: '' });
    setNavItems([]); setSelNav(new Set());
  };

  const saveMac = () => {
    // Field highlight + centred warning dialog (ALPACA LIMS validation parity).
    const errs: { macName?: string; moduleName?: string } = {};
    if (!form.moduleName.trim()) errs.moduleName = 'Module is required.';
    if (!form.macName.trim()) errs.macName = '"MAC Name" must not be empty.';
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      const msgs = Object.values(errs).join('\n');
      notify.warning(msgs);
      return;
    }
    setFormErrors({});
    setSaving(true);
    const bodyData = { moduleName: form.moduleName.trim(), moduleIndex: form.moduleIndex || 0, macName: form.macName.trim(), description: form.description.trim() || null };
    runSave({
      save: async () => {
        if (mode === 'new') {
          const r = await api.post('/api/v1/security/mac', bodyData);
          const d = one(r.data);
          // If nav items are pre-filled (Copy MAC flow), persist them now.
          if (d && navItems.length > 0) {
            const newOid = String(d.oid ?? '');
            if (newOid) {
              await api.put(`/api/v1/security/mac/${newOid}/navitems`, {
                items: navItems.map(i => ({ navItemOid: i.navItemOid, read: i.read, write: i.write, create: i.create, delete: i.delete })),
              });
            }
          }
          load();
          // After the first save the slim New form becomes the full detail.
          if (d) openEdit(mapMac(d));
        } else if (selOid) {
          await api.put(`/api/v1/security/mac/${selOid}`, bodyData);
          // ALPACA parity: the NavigationItem rights child grid saves with the
          // record's Save (same EXISTING PUT the old "Save items" button used).
          await api.put(`/api/v1/security/mac/${selOid}/navitems`, {
            items: navItems.map(i => ({ navItemOid: i.navItemOid, read: i.read, write: i.write, create: i.create, delete: i.delete })),
          });
          load();
        }
      },
      successMessage: mode === 'new' ? 'Access level created successfully.' : 'Access level saved successfully.',
      // ALPACA parity: after a successful save the detail returns to read-only
      // VIEW mode (a New level re-opens via openEdit, which already lands there).
      onSaved: () => { if (mode === 'edit') setMode('view'); },
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSaving(false));
  };

  const openEditSelected = () => {
    if (sel.size === 0) { notify.warning('Select one row to edit.'); return; }
    if (sel.size > 1) { notify.warning('Select only one row to edit.'); return; }
    const id = [...sel][0];
    const m = macs.find(x => x.oid === String(id));
    if (m) openEdit(m);
  };

  const delSelected = async () => {
    const targets = macs.filter(m => sel.has(m.oid));
    if (targets.length === 0) { notify.warning('Select at least one row to delete.'); return; }
    const names = targets.map(t => t.macId ?? t.macName ?? '').join(', ');
    if (!(await confirm.confirm({ title: 'Delete access level?', message: `Delete "${names}"? (soft-delete — recoverable)` }))) return;
    try {
      for (const t of targets) await api.delete(`/api/v1/security/mac/${t.oid}`);
      notify.success('Access level deleted.');
      setSel(new Set());
      if (selOid && targets.some(t => t.oid === selOid)) closeDetail();
      load();
    } catch (e) { notify.error(errMsg(e)); }
  };

  // Landing-list order: MACID asc (ALPACA sort), then the global search filter.
  const shownMacs = useMemo(() => {
    const t = search.trim().toLowerCase();
    const f = t
      ? macs.filter(m => (m.macId ?? '').toLowerCase().includes(t)
          || (m.description ?? '').toLowerCase().includes(t)
          || (m.macName ?? '').toLowerCase().includes(t)
          || (m.moduleName ?? '').toLowerCase().includes(t))
      : macs;
    return [...f].sort((a, b) => (a.macId ?? '').localeCompare(b.macId ?? ''));
  }, [macs, search]);

  // ── UI-8: inline (in-cell) editing of Description ONLY (MACID is auto-
  //    generated — read-only).  Double-click the cell → input appears →
  //    Enter/blur commits via the EXISTING MAC PUT → useNotify; Escape reverts.
  //    Single click keeps opening the detail (deferred 250 ms so a double-click
  //    can cancel it).  Standing rule 11: the reason gate wraps the audited PUT.
  //    §9 bulk copy on this LIST grid is WIRED (2026-07-02): right-click a filled
  //    Description cell → shared CellContextMenu → Copy to All / Selected →
  //    PUT /api/v1/security/mac/bulk-field (ONE request, ONE audit row).  Bulk
  //    copy on the detail rights grid + Role Matrix below stays local state +
  //    one audited Save.
  const gateReason = useReasonGate();
  const [editOid, setEditOid] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const clickTimer = useRef<number | null>(null);

  const commitInlineEdit = async () => {
    if (!editOid || savingCell) return;
    const row = macs.find(m => m.oid === editOid);
    if (!row) { setEditOid(null); return; }
    const draft = editDraft.trim();
    if (draft === (row.description ?? '')) { setEditOid(null); return; }
    const reason = await gateReason('Module Access Control', 'edit');
    if (reason === null) { setEditOid(null); return; }
    setSavingCell(true);
    try {
      // Same EXISTING MAC PUT the detail Save uses — only Description changes,
      // the other fields round-trip unchanged from the list row.
      await runWithAuditReason(reason, () =>
        api.put(`/api/v1/security/mac/${row.oid}`, {
          moduleName: row.moduleName ?? '', moduleIndex: row.moduleIndex || 0,
          macName: row.macName ?? '', description: draft || null,
        }));
      setEditOid(null);
      notify.success('Change saved.');
      load();
    } catch (e) { notify.error(errMsg(e)); }
    finally { setSavingCell(false); }
  };

  // §9 bulk copy (2026-07-02) — Description → PUT /api/v1/security/mac/bulk-field
  // (ONE request, ONE audit row).  All = current filtered list; Selected = checkboxes.
  const [descCtx, setDescCtx] = useState<{ x: number; y: number; row: Mac } | null>(null);

  const openDescCtx = (e: React.MouseEvent, row: Mac) => {
    e.preventDefault(); e.stopPropagation();
    if (!canBulkCopy(row.description)) { notify.warning('Cell is empty.'); return; }
    setDescCtx({ x: e.clientX, y: e.clientY, row });
  };

  const bulkApplyDesc = async (scope: 'all' | 'selected') => {
    if (!descCtx) return;
    const value = descCtx.row.description ?? '';
    const oids = scope === 'all' ? shownMacs.map(m => m.oid) : Array.from(sel).map(String);
    if (oids.length === 0) { notify.warning('Select rows first.'); return; }
    // Reason-for-change gate (standing rule 11) — a bulk copy is an Update.
    const reason = await gateReason('Module Access Control', 'edit');
    if (reason === null) return;
    try {
      await runWithAuditReason(reason, () =>
        api.put('/api/v1/security/mac/bulk-field', { field: 'description', value, oids }));
      notify.success(`Description copied to ${oids.length} level${oids.length === 1 ? '' : 's'}.`);
      load();
    } catch (e) { notify.error(errMsg(e)); }
  };

  const descriptionCell = (row: Mac) => {
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    if (editOid === row.oid) {
      return (
        <span onClick={stop} onDoubleClick={stop}>
          <input autoFocus className="ai-inline-edit" value={editDraft} disabled={savingCell}
            onChange={e => setEditDraft(e.target.value)}
            onBlur={() => { void commitInlineEdit(); }}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); void commitInlineEdit(); }
              else if (e.key === 'Escape') { e.preventDefault(); setEditOid(null); }
            }} />
        </span>
      );
    }
    return (
      <span className="ai-editcell" title="Double-click to edit"
        onClick={e => {
          // Defer the row's open-detail so a double-click can cancel it.
          e.stopPropagation();
          if (clickTimer.current) window.clearTimeout(clickTimer.current);
          clickTimer.current = window.setTimeout(() => { clickTimer.current = null; openEdit(row); }, 250);
        }}
        onDoubleClick={e => {
          e.stopPropagation();
          if (clickTimer.current) { window.clearTimeout(clickTimer.current); clickTimer.current = null; }
          if (!perm.write) return;   // Read-only role: no inline description edit
          setEditDraft(row.description ?? ''); setEditOid(row.oid);
        }}
        onContextMenu={e => { if (perm.write) openDescCtx(e, row); }}>
        {row.description ?? ''}
      </span>
    );
  };

  const macCols: AppColumn<Mac>[] = [
    // ALPACA-style per-row edit glyph — first data column, right after the
    // selection checkbox (AppDataGrid renders `selectable` checkboxes itself).
    { field: '__edit', header: 'Edit', width: 44, align: 'center',
      sortable: false, filterable: false,
      render: r => (
        <button type="button" className="sec-rowedit-btn" title="Edit"
          onClick={e => { e.stopPropagation(); openEdit(r); }}>
          <i className="ti ti-pencil" />
        </button>
      ) },
    { field: 'macId', header: 'MACID', width: 360 },
    { field: 'description', header: 'Description',
      text: m => m.description ?? '', render: descriptionCell },
  ];

  // Full-format "Export to" (all 10 ALPACA formats) of the currently-filtered
  // Access Levels list — same MACID + Description columns as the legacy CSV —
  // via the shared <ExportMenu> (AppToolbar exportData).  (The Role Matrix tab
  // keeps its bespoke dynamic-role-column CSV export — see exportMatrixCsv.)
  const levelsExportData: ExportData = {
    filename: 'module-access-control',
    headers: ['MACID', 'Description'],
    rows: shownMacs.map(m => [m.macId ?? '', m.description ?? '']),
  };

  const exportNavItemsCsv = () =>
    void exportRows(navItems, [
      { header: 'Navigation Item', value: i => i.caption },
      { header: 'Read', value: i => i.read ? 'Y' : '' },
      { header: 'Write', value: i => i.write ? 'Y' : '' },
      { header: 'Create', value: i => i.create ? 'Y' : '' },
      { header: 'Delete', value: i => i.delete ? 'Y' : '' },
    ], { filename: 'mac-navigation-items' });

  // Full-width rights grid: Navigation Item (read-only) + inline R/W/C/D
  // checkboxes (existing toggle logic; Read stays the Intelligence behaviour).
  // Checkboxes are disabled in read-only VIEW mode (Edit unlocks them).
  const rightsCols: AppColumn<NavPerm>[] = [
    { field: 'caption', header: 'Navigation Item', minWidth: 260 },
    ...RIGHT_FLAGS.map(({ key, label }): AppColumn<NavPerm> => ({
      field: key, header: label, width: 90, align: 'center',
      sortable: false, filterable: false,
      render: r => (
        // §9 bulk copy: right-click the checkbox cell (edit mode only) →
        // Copy to All Cells / Copy to Selected Rows via the shared menu.
        <span onContextMenu={e => openRightsCtx(e, key, label, r[key])}>
          <input type="checkbox" checked={r[key]} disabled={mode === 'view' || !perm.write}
            onChange={() => toggleNav(r.navItemOid, key)} />
        </span>
      ),
    })),
  ];

  // Prev / next record arrows walk the current (sorted + filtered) list order.
  const idx = selOid ? shownMacs.findIndex(m => m.oid === selOid) : -1;
  const prevMac = idx > 0 ? (shownMacs[idx - 1] ?? null) : null;
  const nextMac = idx >= 0 && idx < shownMacs.length - 1 ? (shownMacs[idx + 1] ?? null) : null;
  const curMac = selOid ? macs.find(m => m.oid === selOid) ?? null : null;

  // ── matrix tab ──────────────────────────────────────────────────────────
  const cell = (macOid: string, roleId: string) => (grid[macOid] ?? []).includes(lc(roleId));
  const toggleCell = (macOid: string, roleId: string) => {
    setGrid(g => {
      const cur = g[macOid] ?? [];
      const next = cur.includes(lc(roleId)) ? cur.filter(x => x !== lc(roleId)) : [...cur, lc(roleId)];
      return { ...g, [macOid]: next };
    });
    setDirty(d => new Set(d).add(macOid));
  };
  const filteredRows = useMemo(() => {
    const t = matrixFilter.trim().toLowerCase();
    return t ? rows.filter(r => (r.macId ?? '').toLowerCase().includes(t) || (r.moduleName ?? '').toLowerCase().includes(t)) : rows;
  }, [rows, matrixFilter]);

  const saveGrid = () => {
    if (dirty.size === 0) { notify.info('No changes to save.'); return; }
    setSavingGrid(true);
    runSave({
      save: async () => {
        for (const macOid of dirty) await api.put(`/api/v1/security/mac/${macOid}/roles`, { roleIds: grid[macOid] ?? [] });
        load();
      },
      successMessage: `Saved ${dirty.size} access level(s).`,
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSavingGrid(false));
  };

  const exportMatrixCsv = () =>
    void exportRows(filteredRows, [
      { header: 'MACID', value: row => row.macId ?? row.macName ?? '' },
      ...roles.map(r => ({ header: r.name, value: (row: MatrixRow) => (cell(row.macOid, r.id) ? 'Y' : '') })),
    ], { filename: 'role-based-mac' });

  let lastMod = '';

  // Keyboard shortcut (CLAUDE.md standing rule 14, §18.6, ADR-0013): the Role
  // Matrix tab's left-slot Save — mod+s, mirroring the button's handler
  // (saveGrid) and its disabled state.  Registered unconditionally ABOVE the
  // first early return; present only on the matrix tab (that is where the Save
  // button renders) so the levels-tab detail never binds a phantom mod+s.  The
  // levels list/detail actions are the shared <AppToolbar> / detail bar and are
  // not bound here.
  useHotkeys(
    tab === 'matrix'
      ? [{ combo: 'mod+s', label: 'Save', group: 'Module Access Control', order: 1,
           run: () => saveGrid(), disabled: savingGrid || dirty.size === 0 || !perm.write }]
      : [],
  );

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the LEVELS
  // detail action bar (sec-dbar, rendered TOP + BOTTOM — bound ONCE here in the
  // parent, not inside detailBar). View-mode CRUD launchers and the edit-mode
  // Save/Reset/Cancel are gated by `mode` to match their render conditions.
  useHotkeys(
    tab === 'levels' && mode !== 'list'
      ? (mode === 'view'
          ? [
              { combo: 'alt+n', label: 'New', group: 'Module Access Control', order: 1, disabled: !perm.create, run: () => openNew() },
              { combo: 'alt+e', label: 'Edit', group: 'Module Access Control', order: 2, disabled: !perm.write, run: () => setMode('edit') },
              { combo: 'alt+d', label: 'Delete', group: 'Module Access Control', order: 3, disabled: !curMac || !perm.delete, run: () => { void deleteCurrent(); } },
              { combo: 'alt+r', label: 'Refresh', group: 'Module Access Control', order: 4, run: () => refreshCurrent() },
              { combo: 'esc', label: 'Close', group: 'Module Access Control', order: 5, allowInInput: false, run: () => closeDetail() },
            ]
          : [
              { combo: 'mod+s', label: 'Save', group: 'Module Access Control', order: 1, disabled: saving, run: () => saveMac() },
              { combo: 'alt+shift+r', label: 'Reset', group: 'Module Access Control', order: 2, disabled: saving, run: () => resetMac() },
              { combo: 'esc', label: 'Cancel', group: 'Module Access Control', order: 3, allowInInput: false, disabled: saving, run: () => closeDetail() },
            ])
      : [],
  );

  // ═══════════════════ LEVELS — FULL-PAGE DETAIL (replaces the list) ═══════
  if (tab === 'levels' && mode !== 'list') {
    // VIEW mode (ALPACA parity): read-only fields, Edit + Close action bar.
    const ro = mode === 'view';
    const recordName = mode === 'new' ? 'New Access Level' : (curMac?.macId ?? (form.macName || 'Access Level'));
    const detailBar = (bottom: boolean) => (
      <div className={`sec-dbar${bottom ? ' bottom' : ''}`}>
        {ro ? (
          <>
            {/* Canonical AppToolbar order (CLAUDE.md §18.6): New · Edit ·
                Delete · Refresh — Audit Trail lives on the title row, not
                here (BO UI Standard v2 ruling 2026-07-01). */}
            {perm.create && (
              <button className="ca-btn" onClick={openNew} title={`New (${formatCombo('alt+n')})`}>
                <span className="tb-i tb-i-green">＋</span> New
              </button>
            )}
            {perm.write && (
              <button className="ca-btn" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}>
                <span className="tb-i tb-i-blue">✎</span> Edit
              </button>
            )}
            {perm.delete && (
              <button className="ca-btn" onClick={deleteCurrent} disabled={!curMac} title={`Delete (${formatCombo('alt+d')})`}>
                <span className="tb-i tb-i-red">🗑</span> Delete
              </button>
            )}
            <button className="ca-btn" onClick={refreshCurrent} title={`Refresh (${formatCombo('alt+r')})`}>
              <span className="tb-i tb-i-green">🔄</span> Refresh
            </button>
            <button className="ca-btn sec-dcancel" onClick={closeDetail} title={`Close (${formatCombo('esc')})`}>
              <span className="tb-i">↩︎</span> Close
            </button>
          </>
        ) : (
          <>
            <button className="ca-btn ca-btn-solid" onClick={saveMac} disabled={saving} title={`Save (${formatCombo('mod+s')})`}>
              <span className="tb-i">💾</span> Save
            </button>
            {/* ALPACA Reset (MACController.resetMACAction) — clears the detail
                locally; blocked while any role links this MAC. */}
            <button className="ca-btn" onClick={resetMac} disabled={saving} title={`Reset (${formatCombo('alt+shift+r')})`}>
              <i className="ti ti-eraser" /> Reset
            </button>
            <button className="ca-btn sec-dcancel" onClick={closeDetail} disabled={saving} title={`Cancel (${formatCombo('esc')})`}>
              <span className="tb-i">↩︎</span> Cancel
            </button>
          </>
        )}
      </div>
    );

    return (
      <div className="sec-page">
        {/* List/Detail segmented switch — Detail active; List returns to the grid. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') closeDetail(); }} />
        </div>
        <div className="sec-lbody sec-lbody-detail">
          <div className="sec-dwrap">
            <div className="sec-dhead">
              <div>
                <h2>
                  Module Access Control – {recordName}
                  {mode !== 'new' && selOid && (
                    <AuditTrailButton variant="chip" formName="Module Access Control" sourceId={selOid} />
                  )}
                </h2>
                <div className="sec-dbc">
                  <span>Home</span><i className="ti ti-chevron-right" />
                  <span>User &amp; Security</span><i className="ti ti-chevron-right" />
                  <a onClick={closeDetail}>Module Access Control</a><i className="ti ti-chevron-right" />
                  <span>{recordName}</span>
                </div>
              </div>
              <div className="sec-dnav">
                <button className="sec-na" title="Previous record" disabled={!prevMac || saving}
                  onClick={() => prevMac && openEdit(prevMac)}>‹</button>
                <button className="sec-na next" title="Next record" disabled={!nextMac || saving}
                  onClick={() => nextMac && openEdit(nextMac)}>›</button>
              </div>
            </div>

            {detailBar(false)}

            <div className="sec-dbody">
              <h3 className="sec-dsect">General Information</h3>
              {/* ALPACA MAC DetailView parity: 2-column header.
                  col 1 = MAC ID (auto) · Module (lookup) · Module Index (auto);
                  col 2 = MAC Name* · Description. */}
              <div className="sec-dcols">
                <div className="sec-dcol">
                  <div className="sec-field">
                    <label>MAC ID</label>
                    <input className="sec-input readonly" readOnly
                      value={curMac?.macId ?? ''}
                      placeholder="Autogenerate" title="Auto-generated on save" />
                  </div>
                  <div className="sec-field">
                    <label className="sec-req-lbl">Module <span className="sec-req">*</span></label>
                    <ComboSelect value={form.moduleName} options={modules.map(m => m.moduleName)} allowCustom
                      placeholder="Select a module…" disabled={ro}
                      onChange={v => {
                        const mref = modules.find(m => m.moduleName === v);
                        setForm(f => ({ ...f, moduleName: v, moduleIndex: mref ? mref.moduleIndex : f.moduleIndex }));
                        if (formErrors.moduleName && v.trim()) setFormErrors(fe => ({ ...fe, moduleName: undefined }));
                      }} />
                  </div>
                  <div className="sec-field">
                    <label>Module Index</label>
                    <input className="sec-input readonly" readOnly value={form.moduleIndex} />
                  </div>
                </div>
                <div className="sec-dcol">
                  <div className="sec-field">
                    <label>MAC Name <span className="sec-req">*</span></label>
                    <input className={`sec-input${ro ? ' readonly' : formErrors.macName ? ' required-empty' : ''}`}
                      value={form.macName} readOnly={ro} disabled={ro}
                      onChange={e => {
                        setForm(f => ({ ...f, macName: e.target.value }));
                        if (formErrors.macName && e.target.value.trim()) setFormErrors(fe => ({ ...fe, macName: undefined }));
                      }}
                      />
                  </div>
                  <div className="sec-field">
                    <label>Description</label>
                    <textarea className={`sec-input${ro ? ' readonly' : ''}`} rows={3} value={form.description}
                      readOnly={ro} disabled={ro}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="R / R/W / R/W/C/D" />
                  </div>
                </div>
              </div>

              {(mode !== 'new' || navItems.length > 0) && (
                <div className="sec-child">
                  {/* Full-width NavigationItem rights grid (NOT a tab) —
                      Reset lives on the detail action bar (legacy
                      resetMACAction port, 2026-07-02).  VIEW mode: grid
                      visible, Link/Unlink hidden, checkboxes disabled until
                      Edit.  Also shown in 'new' mode when Copy MAC pre-fills
                      nav items. */}
                  <h3 className="sec-dsect">
                    Navigation Items
                    <span className="tools">
                      {!ro && (
                        <>
                          <button className="ca-btn" onClick={() => void openNavPicker()}>
                            <i className="ti ti-link" /> Link
                          </button>
                          <button className="ca-btn" onClick={unlinkSelectedNav} disabled={selNav.size === 0}>
                            <i className="ti ti-unlink" /> Unlink
                          </button>
                        </>
                      )}
                      <button className="ca-btn" onClick={exportNavItemsCsv} disabled={navItems.length === 0}>
                        <span className="tb-i tb-i-teal">📤</span> Export to
                      </button>
                    </span>
                  </h3>
                  <AppDataGrid<NavPerm>
                    rows={navItems}
                    columns={rightsCols}
                    getRowId={i => i.navItemOid}
                    selectable
                    selectedIds={selNav}
                    onSelectionChange={setSelNav}
                    emptyText="No navigation items linked — use Link to add one."
                    defaultPageSize={20}
                  />
                </div>
              )}
            </div>

            {detailBar(true)}
          </div>
        </div>

        {/* Shared Link picker — navigation items not yet linked on the left. */}
        <DualListPickerDialog
          open={navPickerOpen}
          title="Link Navigation Items"
          icon="ti-list-check"
          // The module's forms, plus anything already linked (so a form outside the module list still shows on the Selected side).
          available={[...allNav.map(n => ({ id: n.oid, label: n.caption })),
            ...navItems.filter(i => !allNav.some(n => n.oid === i.navItemOid)).map(i => ({ id: i.navItemOid, label: i.caption }))]}
          selectedIds={navItems.map(i => i.navItemOid)}
          onOk={applyNavPick}
          onCancel={() => setNavPickerOpen(false)}
        />

        {/* §9 bulk-copy menu for the rights grid (local state; Save persists + audits). */}
        {rightsCtx && (
          <CellContextMenu
            value={rightsCtx.value}
            columnLabel={rightsCtx.label}
            selectedCount={selNav.size}
            position={{ x: rightsCtx.x, y: rightsCtx.y }}
            onCopyAll={() => applyRightsAll(rightsCtx.key, rightsCtx.value)}
            onCopySelected={() => applyRightsSelected(rightsCtx.key, rightsCtx.value)}
            onClose={() => setRightsCtx(null)}
          />
        )}
      </div>
    );
  }

  // ═══════════════════ LIST VIEWS (landing) ═══════════════════
  return (
    <div className="sec-page">
      <div className="sec-head">
        {/* List/Detail segmented switch (BO UI Standard) — levels tab only; the
            Role Matrix is a single view. Detail opens the checkbox-selected
            row (exactly one) or the first row of the current list. */}
        {tab === 'levels' && (
          <div style={{ padding: '10px 0 0' }}>
            <ViewToggle view="list" detailDisabled={shownMacs.length === 0}
              onChange={(v) => {
                if (v !== 'detail') return;
                const m = sel.size === 1
                  ? macs.find(x => x.oid === String(Array.from(sel)[0]))
                  : shownMacs[0];
                if (m) openEdit(m);
              }} />
          </div>
        )}
        <FormTitle pageId={tab === 'matrix' ? 'sec-rolemac' : 'sec-mac'} loadMs={loadMs} loading={loading} auditFormName="Module Access Control" />
        <div className="sec-sub">
          {tab === 'matrix'
            ? `${rows.length} level${rows.length === 1 ? '' : 's'} × ${roles.length} role${roles.length === 1 ? '' : 's'}`
            : `${macs.length} item${macs.length === 1 ? '' : 's'}`}
        </div>
      </div>

      {err && <div className="sec-error">{err}</div>}

      {/* ── LEVELS TAB — list landing ── */}
      {tab === 'levels' && (
        <div className="sec-lbody">
          {/* A role without the right does not SEE the action (hidden, not greyed). */}
          <AppToolbar
            onNew={perm.create ? openNew : undefined}
            onEdit={perm.write ? openEditSelected : undefined}
            editDisabled={sel.size !== 1}
            onDelete={perm.delete ? delSelected : undefined}
            deleteDisabled={sel.size === 0}
            beforeRefresh={perm.create ? (
              <button className="ca-btn" onClick={copyMacSelected} disabled={sel.size !== 1} title="Copy MAC — duplicate the selected level into a new one">
                <span className="tb-i tb-i-blue">⧉</span> Copy MAC
              </button>
            ) : undefined}
            onRefresh={load}
            refreshing={loading}
            exportData={levelsExportData}
          />
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input placeholder="Search access levels…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="sec-gridcard">
            <AppDataGrid<Mac>
              rows={shownMacs}
              columns={macCols}
              initialSort={{ key: 'macId', dir: 'asc' }}
              getRowId={m => m.oid}
              loading={loading}
              selectable
              selectedIds={sel}
              onSelectionChange={setSel}
              onRowClick={openEdit}
              emptyText="No access levels."
            />
          </div>

          {/* §9 bulk-copy menu (2026-07-02) — Description → the bulk endpoint
              (ONE request, ONE audit row). */}
          {descCtx && (
            <CellContextMenu
              value={descCtx.row.description}
              columnLabel="Description"
              selectedCount={sel.size}
              position={{ x: descCtx.x, y: descCtx.y }}
              onCopyAll={() => void bulkApplyDesc('all')}
              onCopySelected={() => void bulkApplyDesc('selected')}
              onClose={() => setDescCtx(null)}
            />
          )}
        </div>
      )}

      {/* ── MATRIX TAB — single matrix view (no detail) ── */}
      {tab === 'matrix' && (
        <div className="sec-lbody">
          <AppToolbar
            left={perm.write ? (
              <button className="ca-btn ca-btn-solid" onClick={saveGrid} disabled={savingGrid || dirty.size === 0}
                title={`Save (${formatCombo('mod+s')})`}>
                <span className="tb-i">💾</span> Save
              </button>
            ) : undefined}
            exportOptions={[{ label: 'CSV', icon: 'ti-file-type-csv', onSelect: exportMatrixCsv }]}
          />
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input placeholder="Filter access levels…" value={matrixFilter} onChange={e => setMatrixFilter(e.target.value)} />
              {matrixFilter && <span className="list-search-clear" onClick={() => setMatrixFilter('')}>×</span>}
            </div>
            <span className="sec-count">
              {filteredRows.length} level{filteredRows.length === 1 ? '' : 's'} × {roles.length} role{roles.length === 1 ? '' : 's'}
              {dirty.size > 0 && <b style={{ color: '#b5560c' }}> · {dirty.size} unsaved</b>}
            </span>
          </div>
          <div className="sec-matrix-wrap">
            <table className="sec-matrix">
              <thead>
                <tr>
                  <th className="lvl">MAC ID</th>
                  {roles.map(r => <th key={r.id} className="role"><span title={r.name}>{r.name}</span></th>)}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => {
                  const mod = row.moduleName ?? '';
                  const showMod = mod !== lastMod; lastMod = mod;
                  return (
                    <tr key={row.macOid} className={dirty.has(row.macOid) ? 'dirty' : ''}>
                      <td className="lvl">
                        {showMod && <div className="sub" style={{ marginBottom: 2 }}>{mod}</div>}
                        <span className="mid">{row.macId || row.macName}</span>{row.macId && row.macName ? <span className="sub"> · {row.macName}</span> : null}
                      </td>
                      {roles.map(r => (
                        <td key={r.id} className="cell"
                          onContextMenu={e => openMatrixCtx(e, r.id, r.name, cell(row.macOid, r.id))}>
                          <input type="checkbox" checked={cell(row.macOid, r.id)} disabled={!perm.write} onChange={() => toggleCell(row.macOid, r.id)} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sec-hint">Tick = the role holds that module access level. ALPACA cascades the downstream nav-permission regeneration for affected users on its next save.</div>

          {/* §9 bulk-copy menu for the matrix — the matrix has no row-selection
              checkboxes, so only Copy to All Cells is offered (whole role column). */}
          {matrixCtx && (
            <CellContextMenu
              value={matrixCtx.value}
              columnLabel={matrixCtx.roleName}
              position={{ x: matrixCtx.x, y: matrixCtx.y }}
              onCopyAll={() => applyMatrixAll(matrixCtx.roleId, matrixCtx.value)}
              onClose={() => setMatrixCtx(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
